import React, { useState, useEffect, useCallback } from "react";
import { useCookies } from "react-cookie";
import { withRouter, useRouteMatch } from "react-router-dom";
import { fetchUserProfile } from "../api/xchange-api";
import { APP_EVENT_ID, APP_BRAND_PUB_ID } from "../utils/consts";

// TODO: This is completely for demo purposes... this should be
// re-worked to make use of REAL auth

function getCookieStringValue(cookie, key) {
  if (!cookie) {
    return false;
  }
  const regex = new RegExp(`${key}=((?:\\w* ?)*)`);
  const match = cookie.match(regex);
  if (match && match.length) {
    return match[1];
  }

  return false;
}

export const USER_ROLES = {
  ADMIN: "Admin",
  RECEPTIONIST: "Receptionist",
  DELEGATE: "Delegate",
  HOST: "Host",
};

export const USER_COMPANY_TYPE = {
  PAVILIONS: "Pavilions",
};

const UserContext = React.createContext({});

const UserProvider = withRouter(({ children, history }) => {
  const [loadingUserProfile, setLoadingUserProfile] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [role, setRole] = useState(null);
  const [companyId, setCompanyId] = useState(undefined);
  const [meetingId, setMeetingId] = useState(undefined);
  const [isInReceptionRoom, setIsInReceptionRoom] = useState(false);
  const [isHome, setIsHome] = useState(false);
  const [cookies] = useCookies(["User"]);
  const userCookie = cookies.User;
  const fullName = getCookieStringValue(userCookie, "_fn");
  const userId = getCookieStringValue(userCookie, "_uid");
  if (!userCookie || !userId || !fullName) {
    // redirect user to login screen if no "User" cookie exists
    window.location.href = `${process.env.REACT_APP_LOGIN_REDIRECT_URL}?ReturnUrl=${window.location.href}`;
    return null;
  }

  // Grab params from URL
  let meetingPath = useRouteMatch("/:companyId/meeting/:meetingId");
  let receptionPath = useRouteMatch("/:companyId/reception/:meetingId?");
  let homePath = useRouteMatch("/:companyId/home");

  const setUserRole = useCallback((profile, companyId) => {
    if (!profile || !companyId) {
      return;
    }

    if (!profile.ExhibitorInformation) {
      setRole(USER_ROLES.DELEGATE);
      return;
    }

    const exhibitorInfo = profile.ExhibitorInformation;
    let _role = USER_ROLES.DELEGATE;
    if (exhibitorInfo.CompanyProfileID.toString() === companyId) {
      if (exhibitorInfo.CompanyProfileUserRole === USER_ROLES.RECEPTIONIST) {
        _role = USER_ROLES.RECEPTIONIST;
      } else if (exhibitorInfo.CompanyProfileUserRole === USER_ROLES.ADMIN) {
        _role = USER_ROLES.ADMIN;
      } else {
        _role = USER_ROLES.HOST;
      }
    }
    setRole(_role);
  }, []);

  // Load User Profile
  useEffect(() => {
    const getUserProfile = async () => {
      setLoadingUserProfile(false);
      const profile = await fetchUserProfile(
        userId,
        APP_BRAND_PUB_ID,
        APP_EVENT_ID
      );
      if (profile) {
        setUserProfile(profile);
        setUserRole(profile, companyId);
      }
    };

    if (companyId && !userProfile && loadingUserProfile) {
      getUserProfile();
    }
  }, [userId, setUserRole, userProfile, companyId, loadingUserProfile]);

  // Set meeting and company details if we're in a /meeting URL path
  useEffect(() => {
    if (meetingPath && meetingPath.params) {
      let { companyId, meetingId } = meetingPath.params;
      setCompanyId(companyId);
      setMeetingId(meetingId);
      setIsInReceptionRoom(false);
      setIsHome(false);
    }
  }, [meetingPath]);

  // Set meeting and company details if we're in a /reception URL path
  useEffect(() => {
    if (receptionPath && receptionPath.params) {
      let { companyId, meetingId } = receptionPath.params;
      setCompanyId(companyId);
      setMeetingId(meetingId);
      setIsInReceptionRoom(true);
      setIsHome(false);
    }
  }, [receptionPath]);

  useEffect(() => {
    if (homePath) {
      let { companyId } = homePath.params;
      setCompanyId(companyId);
      setIsHome(true);
      setIsInReceptionRoom(false);
    }
  }, [homePath]);

  return (
    <UserContext.Provider
      value={{
        userId,
        fullName,
        userProfile,
        role,
        meetingId,
        companyId,
        isHome,
        isInReceptionRoom,
        loadingUserProfile,
      }}
    >
      {children}
    </UserContext.Provider>
  );
});

export { UserContext, UserProvider };
